import { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isRussia } from 'env'
import _ from 'lodash'
import { refetchFunc } from 'modules/domain/common/hooks'
import {
  ApprovalStatus,
  Deal,
  DealPeriod,
  DealPeriodsTypes,
  DealStatusTypes,
  DealType,
  isApprovalStatusAfter,
} from 'modules/domain/deal/types'
import { Card } from 'views/components/Card/Card'
import { isStatusAfter } from 'views/pages/Deal/DealDetail/helpers'
import {
  IconAccount,
  IconCalendar,
  IconPickup,
  IconSign,
  IconSignTurnoverPlan,
  StatusBody,
  StatusTitle,
  StepContainer,
  StepsColumn,
} from 'views/pages/Deal/DealDetail/styled'
import { ApprovedByExecutiveDirector } from './Steps/ApprovedByExecutiveDirector'
import { TurnoverDaysStep } from './Steps/TurnoverDaysStep'
import { LogisticBody } from './Steps/Logistic/LogisticBody'
import { Overload } from './Steps/Overload/Overload'
import { ApprovedByCommercialDirector } from './Steps/ApprovedByCommercialDirector'
import { useDealContext } from '../../DealContext'
import { DealPeriodStep } from './Steps/DealPeriod/DealPeriodStep'

type StepProps = {
  key: string
  title: string
  isDone: boolean
  Icon: FC<{ $isDone: boolean }>
  body: ReactNode
}

// probably isn't used now
const isLogisticDone = (deal: Deal) => {
  const approvalStatus = deal.approval_status
  const approvalIsOk = isApprovalStatusAfter(approvalStatus, ApprovalStatus.APPROVED_BY_LOGISTICIANS)
  const signIsOk = !isStatusAfter(deal.status, DealStatusTypes.AGREED) || approvalIsOk
  return signIsOk && deal.is_approved_by_logistician
}

const Step: FC<StepProps> = ({ title, isDone, Icon, body }) => {
  return (
    <Card.Container bordered padding="compact">
      <StepContainer>
        <Icon $isDone={isDone} />
        <div>
          <StatusTitle>{title}</StatusTitle>
          <StatusBody>{body}</StatusBody>
        </div>
      </StepContainer>
    </Card.Container>
  )
}

export const DealContractSign: FC<{
  deal: Deal
  dealRefetch: refetchFunc
}> = ({ deal, dealRefetch }) => {
  const { t } = useTranslation('deal')

  const isShowTurnoverSign =
    isRussia &&
    deal.margin_percent < deal.target_margin_percent &&
    isStatusAfter(deal.status, DealStatusTypes.CUSTOMER_NEGOTIATIONS) &&
    !isStatusAfter(deal.status, DealStatusTypes.CONTRACT_SIGNED)

  const [dealPeriodLoading, setDealPeriodLoading] = useState<DealPeriod>()
  const [dealPeriodDelivery, setDealPeriodDelivery] = useState<DealPeriod>()

  const { dealPeriods, progressDealPeriods, dealPeriodRefetch } = useDealContext()

  const refetchDealPeriods = async () => {
    await Promise.all([dealRefetch(), dealPeriodRefetch()])
  }

  const isShowLoadingDealPeriod = isStatusAfter(deal.status, DealStatusTypes.AGREED)
  const isShowDeliveryDealPeriod = isStatusAfter(deal.status, DealStatusTypes.AGREED) && dealPeriodLoading

  useEffect(() => {
    const loadingPeriod = dealPeriods?.find((p) => p.period_type === DealPeriodsTypes.loading)
    if (loadingPeriod) {
      setDealPeriodLoading(loadingPeriod)
    }
    const deliveryPeriod = dealPeriods?.find((p) => p.period_type === DealPeriodsTypes.delivery)
    if (deliveryPeriod) {
      setDealPeriodDelivery(deliveryPeriod)
    }
  }, [dealPeriods])

  const statuses = {
    dealPeriodLoading: !!dealPeriodLoading,
    dealPeriodDelivery: !!dealPeriodDelivery,
    logistic: isLogisticDone(deal),
    commercialDirector: isApprovalStatusAfter(deal.approval_status, ApprovalStatus.APPROVED_BY_COMMERCIAL_DIRECTOR),
    executiveDirector: isApprovalStatusAfter(deal.approval_status, ApprovalStatus.APPROVED_BY_EXECUTIVE_DIRECTOR),
    turnoverPlan: !_.isNil(deal.planned_turnover_days),
    finDir: deal.approved_by_financial_director,
  }

  const steps: StepProps[] = [
    {
      key: 'dealPeriodLoading',
      title: t('contractSign.dealPeriodDcTitle'),
      isDone: statuses.dealPeriodLoading,
      Icon: IconCalendar,
      body: (
        <DealPeriodStep
          dealPeriod={dealPeriodLoading}
          fetchProgress={progressDealPeriods}
          dealPeriodRefetch={refetchDealPeriods}
          type={DealPeriodsTypes.loading}
          dealId={deal.id}
        />
      ),
      isShow: isShowLoadingDealPeriod,
    },
    {
      key: 'dealPeriodDelivery',
      title: t('contractSign.dealPeriodTraderTitle'),
      isDone: statuses.dealPeriodDelivery,
      Icon: IconCalendar,
      body: (
        <DealPeriodStep
          dealPeriod={dealPeriodDelivery}
          fetchProgress={progressDealPeriods}
          dealPeriodRefetch={refetchDealPeriods}
          type={DealPeriodsTypes.delivery}
          dealId={deal.id}
        />
      ),
      isShow: isShowDeliveryDealPeriod,
    },
    {
      key: 'overload',
      title: t('contractSign.loading'),
      isDone: true,
      Icon: IconPickup,
      body: <Overload deal={deal} dealRefetch={dealRefetch} />,
      isShow: true,
    },
    {
      key: 'logistic',
      title: t('contractSign.logisticCalculations'),
      isDone: statuses.logistic,
      Icon: IconAccount,
      body: <LogisticBody deal={deal} dealRefetch={dealRefetch} />,
      isShow: deal.deal_type !== DealType.railway,
    },
    {
      key: 'turnoverPlan',
      title: t('contractSign.turnoverPlan'),
      isDone: statuses.turnoverPlan,
      Icon: IconSignTurnoverPlan,
      body: (
        <TurnoverDaysStep plannedTurnoverDays={deal.planned_turnover_days} dealId={deal.id} dealRefetch={dealRefetch} />
      ),
      showTopDivider: true,
      isShow: isShowTurnoverSign,
    },
    {
      key: 'signCommercialDirector',
      title: t('contractSign.signedByCommectialDirector'),
      isDone: statuses.commercialDirector,
      Icon: IconSign,
      body: <ApprovedByCommercialDirector isDone={statuses.commercialDirector} deal={deal} dealRefetch={dealRefetch} />,
      isShow: isStatusAfter(deal.status, DealStatusTypes.CUSTOMER_NEGOTIATIONS),
    },
    {
      key: 'signExecutiveDirector',
      title: t('contractSign.signedTitle'),
      isDone: statuses.executiveDirector,
      Icon: IconSign,
      body: <ApprovedByExecutiveDirector isDone={statuses.executiveDirector} deal={deal} dealRefetch={dealRefetch} />,
      isShow: isStatusAfter(deal.status, DealStatusTypes.CUSTOMER_NEGOTIATIONS),
    },
  ].filter((step) => step.isShow)

  return (
    <StepsColumn>
      {steps.map((step) => (
        <Step key={step.key} title={step.title} isDone={step.isDone} Icon={step.Icon} body={step.body} />
      ))}
    </StepsColumn>
  )
}
